import React, { useState, useRef, useEffect } from 'react';
import { Download, CheckCircle2, RotateCcw, AlertCircle, Scissors, Eye, Loader2 } from 'lucide-react';
import type { Job, CutRange } from '@vidly/types';
import { BetaFeedbackCard } from './BetaFeedbackCard';

interface RenderStepProps {
  job: Job;
  downloadUrl?: string | null;
  originalVideoUrl?: string | null;
  onNewJob: () => void;
  userEmail?: string;
  isRendering?: boolean;
  renderProgress?: number;
  downloadFilename?: string;
}

export const RenderStep: React.FC<RenderStepProps> = ({
  job,
  downloadUrl,
  originalVideoUrl,
  onNewJob,
  userEmail,
  isRendering = false,
  renderProgress = 0,
  downloadFilename
}) => {
  const isCompleted = job.status === 'completed' && !isRendering;
  const isFailed = job.status === 'failed';

  const ranges: CutRange[] = job.aiPlan?.selectedRanges || [];
  const totalVideoDuration = job.durationSeconds || 38;
  const summarizedDuration = ranges.reduce((acc, r) => acc + (r.end - r.start), 0) || totalVideoDuration;

  const [playMode, setPlayMode] = useState<'cut' | 'full'>('cut');
  const [currentRangeIndex, setCurrentRangeIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatExpiresAt = (epochMs: number) => {
    return new Date(epochMs).toLocaleString('es-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Determine which video source to display
  // If in 'cut' mode, play the rendered cut video (downloadUrl).
  // If in 'full' mode, play the original uncut video (originalVideoUrl).
  const currentVideoSrc = (playMode === 'cut' && downloadUrl)
    ? downloadUrl
    : (originalVideoUrl || downloadUrl || '');

  // Handle video element time updates
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;

      if (e.code === 'Space') {
        e.preventDefault();
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      } else if (e.code === 'ArrowLeft') {
        e.preventDefault();
        video.currentTime = Math.max(0, video.currentTime - 3);
      } else if (e.code === 'ArrowRight') {
        e.preventDefault();
        video.currentTime = Math.min(video.duration || 9999, video.currentTime + 3);
      } else if (e.code === 'KeyM') {
        e.preventDefault();
        video.muted = !video.muted;
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentVideoSrc]);

  const handleTogglePlayMode = (mode: 'cut' | 'full') => {
    setPlayMode(mode);
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  };

  const resolvedFilename = downloadFilename || `resumen-${job.id.substring(0, 8)}.mp4`;

  return (
    <div className="max-w-3xl mx-auto py-6 text-center space-y-6">
      
      {/* Status Header */}
      <div className="space-y-3">
        {isCompleted && (
          <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-2">
            <CheckCircle2 className="w-5 h-5" />
          </div>
        )}
        {isFailed && (
          <div className="w-10 h-10 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center mx-auto mb-2">
            <AlertCircle className="w-5 h-5" />
          </div>
        )}
        {!isCompleted && !isFailed && (
          <div className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 text-emerald-400 flex items-center justify-center mx-auto mb-2 animate-spin">
            <Loader2 className="w-5 h-5" />
          </div>
        )}

        <h3 className="text-xl font-semibold text-white">
          {isCompleted
            ? 'Resumen renderizado con éxito'
            : isFailed
            ? 'Error en el renderizado'
            : 'Renderizando y ensamblando vídeo con cortes exactos...'}
        </h3>

        <p className="text-xs text-zinc-400 max-w-md mx-auto">
          {isCompleted
            ? `Vídeo reducido de ${formatTime(totalVideoDuration)} a ${formatTime(summarizedDuration)} con cortes limpios aplicados sobre tu metraje original.`
            : isFailed
            ? (job.errorMessage || 'Ocurrió un error inesperado durante el procesamiento.')
            : 'Procesando los fragmentos seleccionados para crear el archivo final descargable.'}
        </p>

        {/* Live Rendering Progress Bar */}
        {!isCompleted && !isFailed && (
          <div className="space-y-2 max-w-sm mx-auto pt-2">
            <div className="flex justify-between text-[11px] text-zinc-400 font-mono">
              <span>Progreso de edición</span>
              <span className="text-emerald-400 font-semibold">{renderProgress}%</span>
            </div>
            <div className="h-2 w-full rounded-full bg-zinc-900 border border-zinc-800 overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all duration-300 rounded-full"
                style={{ width: `${Math.max(5, renderProgress)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Video Preview Player */}
      {isCompleted && currentVideoSrc && (
        <div className="rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-950 text-left space-y-0 shadow-lg">
          
          {/* Top Switcher Bar */}
          <div className="p-3 bg-zinc-900/60 border-b border-zinc-800 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1 bg-zinc-950 p-1 rounded-lg border border-zinc-800 text-[11px] sm:text-xs">
              <button
                onClick={() => handleTogglePlayMode('cut')}
                className={`px-2.5 sm:px-3 py-1 rounded-md font-medium transition-all ${
                  playMode === 'cut'
                    ? 'bg-zinc-100 text-zinc-950 font-semibold'
                    : 'text-zinc-400 hover:text-white'
                }`}
              >
                Resumen ({formatTime(summarizedDuration)})
              </button>

              {originalVideoUrl && (
                <button
                  onClick={() => handleTogglePlayMode('full')}
                  className={`px-2.5 sm:px-3 py-1 rounded-md font-medium transition-all ${
                    playMode === 'full'
                      ? 'bg-zinc-100 text-zinc-950 font-semibold'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  Original ({formatTime(totalVideoDuration)})
                </button>
              )}
            </div>

            <span className="text-[11px] text-zinc-400 px-2 py-0.5 rounded bg-zinc-900 border border-zinc-800">
              {playMode === 'cut' ? 'Vídeo recortado listo' : `${ranges.length} cortes seleccionados`}
            </span>
          </div>

          {/* HTML5 Video Element */}
          <div className="relative bg-black flex items-center justify-center">
            <video
              key={currentVideoSrc}
              ref={videoRef}
              controls
              playsInline
              src={currentVideoSrc}
              className="w-full max-h-[440px] bg-black"
            />
          </div>

          {/* Timeline Bar (Highlight cuts if in full view) */}
          {playMode === 'full' && (
            <div className="p-3 bg-zinc-950 border-t border-zinc-800 space-y-1.5">
              <div className="flex justify-between text-[10px] text-zinc-500">
                <span>00:00</span>
                <span>Línea de fragmentos conservados</span>
                <span>{formatTime(totalVideoDuration)}</span>
              </div>

              <div className="h-2 w-full rounded-full bg-zinc-900 relative overflow-hidden flex">
                {ranges.map((r, i) => {
                  const left = (r.start / totalVideoDuration) * 100;
                  const width = ((r.end - r.start) / totalVideoDuration) * 100;
                  return (
                    <div
                      key={i}
                      title={`${r.reason} (${formatTime(r.start)} - ${formatTime(r.end)})`}
                      className="absolute h-full rounded-full bg-emerald-400"
                      style={{ left: `${left}%`, width: `${Math.max(1.5, width)}%` }}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {/* Keyboard Hotkeys Hint */}
          <div className="hidden sm:flex px-3 py-1.5 bg-zinc-950/80 border-t border-zinc-800/60 items-center justify-center gap-3 text-[11px] text-zinc-500">
            <span>Atajos:</span>
            <span>Espacio (Play/Pausa)</span>
            <span>•</span>
            <span>← / → (±3s)</span>
            <span>•</span>
            <span>M (Silenciar)</span>
          </div>
        </div>
      )}

      {/* Download Actions */}
      {isCompleted && (
        <div className="p-5 rounded-xl border border-zinc-800 bg-zinc-900/30 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-left">
          <div className="space-y-0.5">
            <p className="font-medium text-zinc-200">{resolvedFilename}</p>
            <p className="text-zinc-500 text-[11px]">Duración: {formatTime(summarizedDuration)} • Archivo editado listo</p>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <a
              href={downloadUrl || '#'}
              download={resolvedFilename}
              className="btn-primary py-2 px-4 text-xs font-medium flex items-center gap-1.5 flex-1 sm:flex-initial justify-center"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Descargar editado</span>
            </a>

            <button
              onClick={onNewJob}
              className="btn-secondary py-2 px-3.5 text-xs font-medium flex items-center gap-1.5 flex-1 sm:flex-initial justify-center"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Nuevo resumen</span>
            </button>
          </div>
        </div>
      )}

      {/* Beta AI Training & Feedback Card */}
      {isCompleted && (
        <BetaFeedbackCard
          jobId={job.id}
          originalDurationSeconds={totalVideoDuration}
          cutDurationSeconds={summarizedDuration}
          userEmail={userEmail}
        />
      )}

      {isFailed && (
        <div className="p-4 rounded-xl border border-red-900/40 bg-red-950/20 text-xs space-y-3">
          <p className="text-red-300">
            No se pudo completar el corte. Asegúrate de que el vídeo original contenga una pista válida de audio y vídeo.
          </p>
          <button
            onClick={onNewJob}
            className="btn-secondary text-xs py-2 px-4"
          >
            Volver a intentarlo
          </button>
        </div>
      )}

    </div>
  );
};
